import * as React from 'react';

import { useAnalytics } from '@/components/analytics-provider';
import {
  RELEASE_FEATURE_FLAGS,
  resolveReleaseFeatureFlag,
  type ReleaseFeature,
} from '@/lib/features';
import { useDevReleaseFeatureState } from '@/lib/dev-release-features';

export function useReleaseFeatureFlag(feature: ReleaseFeature): boolean | undefined {
  return useReleaseFeatureAccess([feature]);
}

export function useReleaseFeatureAccess(features: readonly ReleaseFeature[]): boolean | undefined {
  const analytics = useAnalytics();
  const development = useDevReleaseFeatureState();
  const featureKey = features.join(',');
  const stableFeatures = React.useMemo(
    () => (featureKey ? (featureKey.split(',') as ReleaseFeature[]) : []),
    [featureKey]
  );
  const readRemoteValue = React.useCallback(() => {
    if (stableFeatures.length === 0) {
      return true;
    }
    if (!analytics.featureFlagsAvailable) {
      return false;
    }

    let hasUnknown = false;
    for (const feature of stableFeatures) {
      const value = analytics.isFeatureEnabled(RELEASE_FEATURE_FLAGS[feature]);
      if (value === false) return false;
      if (value === undefined) hasUnknown = true;
    }
    return hasUnknown ? undefined : true;
  }, [analytics, stableFeatures]);
  const subscribe = React.useCallback(
    (callback: () => void) => analytics.onFeatureFlags(callback),
    [analytics]
  );
  const remoteValue = React.useSyncExternalStore(subscribe, readRemoteValue, readRemoteValue);

  if (stableFeatures.length === 0) return true;

  if (__DEV__) {
    if (!development.hydrated) return undefined;
    return stableFeatures.every((feature) => development.values[feature]);
  }

  return resolveReleaseFeatureFlag(remoteValue);
}
